import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LANG_KEY = 'pakdeheru_language';
const TRANS_CACHE_KEY = 'pakdeheru_translations';

// ========== Language Context ==========
const LanguageContext = createContext(null);

// Module-level state (kept for backward compat with t() outside React tree)
let _currentLang = localStorage.getItem(LANG_KEY) || 'id';
let _uiTranslations = {};
let _contextSetLang = null; // bridge to React context

// Load cached translations
const UI_STRINGS_COUNT_KEY = 'pakdeheru_trans_v';

// Dynamically import UI_STRINGS (will be set by LanguagePicker)
let _uiStrings = {};

export const setUIStrings = (strings) => {
  _uiStrings = strings;
};

const _loadCachedTranslations = () => {
  try {
    const cacheVersion = String(Object.keys(_uiStrings).length);
    const storedVersion = localStorage.getItem(UI_STRINGS_COUNT_KEY);
    if (storedVersion === cacheVersion) {
      const cached = localStorage.getItem(TRANS_CACHE_KEY);
      if (cached) _uiTranslations = JSON.parse(cached);
    } else {
      localStorage.removeItem(TRANS_CACHE_KEY);
      localStorage.setItem(UI_STRINGS_COUNT_KEY, cacheVersion);
    }
  } catch {}
};

// ========== Global t() function (works outside React) ==========
export const t = (key) => {
  if (_currentLang === 'id') return _uiStrings[key] || key;
  const langCache = _uiTranslations[_currentLang];
  if (langCache && langCache[key]) return langCache[key];
  return _uiStrings[key] || key;
};

// ========== Global language setter ==========
export const setLanguage = (lang) => {
  _currentLang = lang;
  localStorage.setItem(LANG_KEY, lang);
  if (_contextSetLang) _contextSetLang(lang);
  window.dispatchEvent(new CustomEvent('language-changed', { detail: { lang } }));
};

export const getCurrentLanguage = () => _currentLang;

// ========== Translate helpers ==========
export const translateText = async (text, targetLang, contentType = 'article') => {
  if (!text || targetLang === 'id') return text;
  try {
    const res = await axios.post(`${BACKEND_URL}/api/translate`, {
      text, target_lang: targetLang, content_type: contentType,
    });
    return res.data.translated;
  } catch {
    return text;
  }
};

// ========== Provider ==========
let _fetchingLang = null;
let _fetchPromise = null;

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(_currentLang);
  const [, forceRender] = useState(0);

  // Bridge context setter to module-level
  useEffect(() => {
    _contextSetLang = setLang;
    return () => { _contextSetLang = null; };
  }, []);

  // Sync module-level state when context lang changes
  useEffect(() => {
    _currentLang = lang;
  }, [lang]);

  // Listen for external language changes (e.g. from t() or setLanguage() calls)
  useEffect(() => {
    const handler = (e) => {
      const newLang = e.detail?.lang;
      if (newLang && newLang !== lang) setLang(newLang);
    };
    window.addEventListener('language-changed', handler);
    return () => window.removeEventListener('language-changed', handler);
  }, [lang]);

  // Fetch translations when language changes
  useEffect(() => {
    if (lang === 'id') return;
    if (!Object.keys(_uiStrings).length) return;

    const langCache = _uiTranslations[lang];
    if (langCache && Object.keys(langCache).length >= Object.keys(_uiStrings).length) return;

    if (_fetchingLang === lang && _fetchPromise) return;

    _fetchingLang = lang;
    _fetchPromise = axios.post(`${BACKEND_URL}/api/translate/batch`, {
      texts: _uiStrings,
      target_lang: lang,
    }).then(res => {
      _uiTranslations[lang] = res.data.translations;
      try {
        localStorage.setItem(TRANS_CACHE_KEY, JSON.stringify(_uiTranslations));
        localStorage.setItem(UI_STRINGS_COUNT_KEY, String(Object.keys(_uiStrings).length));
      } catch {}
      forceRender(n => n + 1);
      window.dispatchEvent(new CustomEvent('ui-translations-loaded', { detail: { lang } }));
    }).catch(() => {}).finally(() => {
      _fetchingLang = null;
      _fetchPromise = null;
    });
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang: setLanguage, t }), [lang]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// ========== Hooks ==========
export const useLanguageContext = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    // Fallback for components outside provider
    return { lang: _currentLang, setLang: setLanguage, t };
  }
  return ctx;
};

// Backward-compatible hooks
export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  const [fallbackLang, setFallbackLang] = useState(_currentLang);

  useEffect(() => {
    if (ctx) return;
    const handler = (e) => setFallbackLang(e.detail?.lang || _currentLang);
    window.addEventListener('language-changed', handler);
    return () => window.removeEventListener('language-changed', handler);
  }, [ctx]);

  return ctx ? ctx.lang : fallbackLang;
};

export const useTranslation = () => {
  const lang = useLanguage();
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const handler = () => forceUpdate(n => n + 1);
    window.addEventListener('ui-translations-loaded', handler);
    return () => window.removeEventListener('ui-translations-loaded', handler);
  }, []);

  return { lang, t };
};

// Initialize cache on module load
export const initTranslationCache = () => {
  _loadCachedTranslations();
};
